import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SecuritySlider from '@/components/SecuritySlider';
import StatusBadge from '@/components/StatusBadge';
import Logo from '@/components/Logo';
import type { SecurityLevel, DeviceInfo } from '@/types/auth';
import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/types/api';

/**
 * Settings page — Security slider, devices, and privacy options.
 */
export default function Settings() {
  const navigate = useNavigate();
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>('balanced');
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [dataDeletion, setDataDeletion] = useState(false);
  const [exportRequested, setExportRequested] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [devicesResp, settingsResp] = await Promise.all([
          apiClient.get<Array<{id: string; device_fingerprint: string; keyboard_layout: string; os: string; browser: string; is_primary: boolean; created_at: string}>>(API_ENDPOINTS.PROFILE_DEVICES),
          apiClient.get<{security_level: string; accept_threshold: number; reject_threshold: number}>(API_ENDPOINTS.SETTINGS_GET),
        ]);

        setDevices(devicesResp.data.map(d => ({
          id: d.id,
          name: d.is_primary ? 'Dispositivo principal' : `Dispositivo ${d.keyboard_layout}`,
          browser: d.browser || 'Desconhecido',
          os: d.os || 'Desconhecido',
          lastUsed: d.created_at,
          trusted: true,
        })));

        setSecurityLevel(settingsResp.data.security_level as SecurityLevel);
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    })();
  }, []);

  const handleSecurityChange = useCallback(async (level: SecurityLevel) => {
    setSecurityLevel(level);
    try {
      await apiClient.patch(API_ENDPOINTS.SETTINGS_UPDATE, { security_level: level });
    } catch (err) {
      console.error('Failed to update security level:', err);
    }
  }, []);

  const handleExportData = useCallback(() => {
    setExportRequested(true);
    setTimeout(() => setExportRequested(false), 3000);
  }, []);

  const handleDeleteProfile = useCallback(() => {
    setDataDeletion(true);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-bg-surface/50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-txt-muted hover:text-txt-primary transition-colors text-sm font-sans"
            >
              &larr; Dashboard
            </button>
          </div>
          <h1 className="text-lg font-semibold text-txt-primary font-sans">
            Configuracoes
          </h1>
          <Logo markOnly markSize="h-7" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Security Level */}
        <section className="p-6 bg-bg-secondary border border-bg-surface rounded-card">
          <h2 className="text-base font-semibold text-txt-primary font-sans mb-1">
            Nivel de Seguranca
          </h2>
          <p className="text-xs text-txt-muted font-sans mb-6">
            Ajuste o equilibrio entre conveniencia e seguranca da autenticacao.
          </p>
          <SecuritySlider
            value={securityLevel}
            onChange={handleSecurityChange}
            showDetails={true}
          />
        </section>

        {/* Devices */}
        <section className="p-6 bg-bg-secondary border border-bg-surface rounded-card">
          <h2 className="text-base font-semibold text-txt-primary font-sans mb-1">
            Dispositivos
          </h2>
          <p className="text-xs text-txt-muted font-sans mb-4">
            Dispositivos que possuem perfil biometrico registrado.
          </p>
          <div className="space-y-3">
            {devices.map((device) => (
              <div
                key={device.id}
                className="flex items-center justify-between p-4 bg-bg-primary/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-bg-surface rounded-lg flex items-center justify-center text-xl">
                    {'💻'}
                  </div>
                  <div>
                    <span className="block text-sm font-medium text-txt-primary font-sans">
                      {device.name}
                    </span>
                    <span className="block text-xs text-txt-muted font-sans">
                      {device.browser} &middot; {device.os}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {device.trusted && (
                    <StatusBadge label="Confiavel" variant="success" />
                  )}
                  <button className="text-xs text-accent-danger hover:text-accent-danger/80 transition-colors font-sans">
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Privacy */}
        <section className="p-6 bg-bg-secondary border border-bg-surface rounded-card">
          <h2 className="text-base font-semibold text-txt-primary font-sans mb-1">
            Privacidade
          </h2>
          <p className="text-xs text-txt-muted font-sans mb-4">
            Controle seus dados biometricos e informacoes pessoais.
          </p>

          <div className="space-y-4">
            {/* Data export */}
            <div className="flex items-center justify-between p-4 bg-bg-primary/50 rounded-lg">
              <div>
                <span className="block text-sm font-medium text-txt-primary font-sans">
                  Exportar meus dados
                </span>
                <span className="block text-xs text-txt-muted font-sans mt-0.5">
                  Baixe uma copia de todas as suas informacoes biometricas.
                </span>
              </div>
              <button
                onClick={handleExportData}
                disabled={exportRequested}
                className="text-sm text-accent-brand hover:text-accent-brand/80 font-sans font-medium transition-colors disabled:opacity-50"
              >
                {exportRequested ? 'Solicitado!' : 'Exportar'}
              </button>
            </div>

            {/* Data processing info */}
            <div className="flex items-center justify-between p-4 bg-bg-primary/50 rounded-lg">
              <div>
                <span className="block text-sm font-medium text-txt-primary font-sans">
                  Processamento local
                </span>
                <span className="block text-xs text-txt-muted font-sans mt-0.5">
                  Features biometricas sao extraidas no seu navegador via Web Worker.
                </span>
              </div>
              <StatusBadge label="Ativo" variant="success" />
            </div>

            {/* Delete profile */}
            <div className="flex items-center justify-between p-4 bg-bg-primary/50 rounded-lg border border-accent-danger/20">
              <div>
                <span className="block text-sm font-medium text-accent-danger font-sans">
                  Excluir perfil biometrico
                </span>
                <span className="block text-xs text-txt-muted font-sans mt-0.5">
                  Remove permanentemente todos os dados biometricos. Esta acao nao pode ser desfeita.
                </span>
              </div>
              {!dataDeletion ? (
                <button
                  onClick={handleDeleteProfile}
                  className="text-sm text-accent-danger hover:text-accent-danger/80 font-sans font-medium transition-colors"
                >
                  Excluir
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await apiClient.delete('/api/v1/profile/biometric');
                        apiClient.clearTokens();
                        navigate('/');
                      } catch (err) {
                        console.error('Failed to delete profile:', err);
                      }
                    }}
                    className="text-xs bg-accent-danger text-white px-3 py-1.5 rounded-lg font-sans font-medium"
                  >
                    Confirmar
                  </button>
                  <button
                    onClick={() => setDataDeletion(false)}
                    className="text-xs text-txt-muted hover:text-txt-primary font-sans transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* About */}
        <section className="p-6 bg-bg-secondary border border-bg-surface rounded-card">
          <h2 className="text-base font-semibold text-txt-primary font-sans mb-1">
            Sobre
          </h2>
          <div className="text-xs text-txt-muted font-sans space-y-1">
            <p>ENI HKS &mdash; Human Keystroke Signature&trade;</p>
            <p>Versao 1.0.0</p>
            <p>Autenticacao biometrica via keystroke dynamics</p>
          </div>
        </section>
      </main>
    </div>
  );
}
